<template>
  <vx-card
    :id="`grid-view-live-camera-${camera.id}${
      isCloseUpCamera ? '-close-up' : ''
    }`"
    class="mb-base overflow-hidden"
    style="padding: 0; margin: 0"
    v-on="$listeners"
  >
    <template slot="no-body">
      <div
        class="flex w-full justify-between items-center relative"
        style="flex-direction: column"
        :style="{ height: gridHeight }"
      >
        <div
          class="flex w-full h-full justify-center items-center live-camera-area"
          :style="{ 'min-height': `calc( 100% - ${cameraInfoBarHeight}px )` }"
        >
          <!-- ローディングメッセージ、エラーメッセージ表示エリア -->
          <P>{{ cameraMsg }}</P>
          <vs-button
            v-if="hasVideoError"
            icon-pack="feather"
            icon="icon-rotate-cw"
            color="rgb(255, 255, 255)"
            type="line"
            size="small"
            @click="connect"
            >{{ retryText }}</vs-button
          >

          <!-- ハイクカムエリア -->
          <div
            v-if="isHykeCam"
            class="hykecam-camera-area"
            ref="hykeCamCameraArea"
          >
            <div class="hykecam-center" :style="{ height: hykeCamHeightPx }">
              <video
                ref="hykeCamVideo"
                class="media-video-player card-video vjs-big-play-centered hykecam-video"
                autoplay
                @click="hykeCamScreen"
              />
            </div>
            <div
              v-show="hykeCamLoaderShow"
              class="hykecam-loader"
              :style="{
                width: hykeCamLoaderWidthPx,
                height: hykeCamLoaderHeightPx,
                top: hykeCamLoaderTopPx,
                left: hykeCamLoaderLeftPx,
              }"
            ></div>
          </div>

          <!-- 通常カメラエリア (HykeCam以外) -->
          <video-player
            v-else-if="!isCloseUpCamera"
            id="live-video-player"
            class="media-video-player card-video vjs-big-play-centered flex w-full h-full justify-center items-center"
            :style="{
              width: liveVideoPlayerHeight,
              'min-width': liveVideoPlayerHeight,
              'max-width': liveVideoPlayerHeight,
            }"
            ref="videoPlayer"
            :options="playerOptions"
            @ready="onPlayerReadied"
            @play="onPlayerPlay"
            @pause="onPlayerPause"
            @error="onPlayerError"
          />

          <!-- クローズアップカメラエリア -->
          <canvas v-if="isCloseUpCamera" id="close-up-canvas"></canvas>
        </div>

        <div
          class="flex w-full h-full items-center"
          :style="{ 'min-height': `${cameraInfoBarHeight}px` }"
        >
          <div class="flex w-full h-full justify-start items-center">
            <div
              ref="cameraInfoArea"
              class="camera-info-area flex w-full h-full justify-between items-start"
            >
              <!-- カメラ情報 表示エリア (CapitalLetter含む) -->
              <div
                class="ml-4"
                @click="onSelectCamera"
                :class="[{ 'cursor-pointer': gridLayout !== 1 }]"
              >
                <div
                  class="flex justify-start items-start ml-0"
                  :style="{ color: AISEA_COLOR.WHITE }"
                >
                  <div
                    class="camera-name-ellipsis"
                    style="font-size: 14px; padding-top: 2px"
                    :style="{
                      width:
                        camera.vesselCapitalLetter.trim().length > 0
                          ? `calc( ${liveCameraAreaWidth}px - 80px )`
                          : `calc( ${liveCameraAreaWidth}px - 50px )`,
                    }"
                  >
                    {{ camera.name }} ({{ camera.description }})
                  </div>
                </div>
              </div>

              <div class="flex justify-end mr-2">
                <div
                  v-if="camera.vesselCapitalLetter.trim().length > 0"
                  class="flex justify-center items-center camera-category-icon"
                  :style="{
                    'background-color':
                      camera.vesselCapitalLetter === 'FIXED'
                        ? AISEA_COLOR.RED
                        : camera.vesselCapitalLetter.trim().length > 0
                        ? AISEA_COLOR.BLUE
                        : null,
                  }"
                >
                  <div
                    class="material-icons flex justify-center items-center"
                    :style="{
                      'font-size':
                        camera.vesselCapitalLetter === 'FIXED'
                          ? '16px'
                          : '12px',
                    }"
                  >
                    {{
                      camera.vesselCapitalLetter === "FIXED"
                        ? "videocam"
                        : camera.vesselCapitalLetter.trim().length > 0
                        ? "directions_boat"
                        : null
                    }}
                  </div>
                </div>

                <vs-dropdown
                  v-show="
                    !hasVideoError || ![2, 5, 3].includes(camera.movieType)
                  "
                  class="cursor-pointer"
                  vs-trigger-click
                >
                  <feather-icon
                    icon="MoreVerticalIcon"
                    svgClasses="w-6 h-6 text-grey"
                    class="mt-1"
                  />
                  <vs-dropdown-menu class="w-34">
                    <vs-dropdown-item
                      v-if="!hasVideoError"
                      @click="toDetailClicked(camera.id, 'realtime')"
                    >
                      <span class="flex items-center detail-link-item">
                        <feather-icon
                          icon="ClockIcon"
                          svgClasses="h-4 w-4"
                          class="mr-2"
                        />
                        <span class="whitespace-no-wrap">リアルタイム</span>
                      </span>
                    </vs-dropdown-item>

                    <vs-dropdown-item
                      v-if="![2, 5, 3].includes(camera.movieType)"
                      @click="toDetailClicked(camera.id, 'history')"
                    >
                      <span class="flex items-center detail-link-item">
                        <feather-icon
                          icon="CalendarIcon"
                          svgClasses="h-4 w-4"
                          class="mr-2"
                        />
                        <span class="whitespace-no-wrap">履歴</span>
                      </span>
                    </vs-dropdown-item>
                  </vs-dropdown-menu>
                </vs-dropdown>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </vx-card>
</template>

<script>
// モジュール
import Vue from "vue";
import videojs from "video.js";
import moment from "moment-timezone";

// Mixin
import { CameraMixin, DeviceInfoMixin, ColorMixin } from "@/mixins";

// コンポーネント
import { videoPlayer } from "vue-videojs7";
import { storageWebCam } from "@/firebase/firebaseConfig";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";

import { createNamespacedHelpers } from "vuex";
const { mapActions } = createNamespacedHelpers("enterpriseCameraList");

export default {
  components: {
    videoPlayer,
  },
  mixins: [CameraMixin, DeviceInfoMixin, ColorMixin],
  props: {
    camera: { type: Object, required: true },
    gridHeight: { type: String, required: false, default: "100%" },
    gridLayout: { type: Number, required: false },
    gridColNum: { type: Number, required: false },
    isCloseUpCamera: { type: Boolean, required: false },
    forcePlayFlg: { type: Boolean, required: false },
  },
  data() {
    return {
      initialized: false,
      hasVideoError: false,
      retryText: "",
      cameraMsg: "",
      hlsSrc: null,
      errorDisplay: null,
      liveCameraAreaWidth: 0,
      liveVideoPlayerHeight: "100%",
      cameraInfoBarHeight: 38,

      // 以下、hykeCam用 data
      hykeCamTimer: null,
      hykeCamUrl: null,
      hykeCamHeight: "100%",
      hykeCamLoaderShow: false,
      hykeCamLoaderHeight: 100,
      hykeCamLoaderWidth: 100,
      hykeCamLoaderTop: 50,
      hykeCamLoaderLeft: 50,
    };
  },
  computed: {
    player() {
      if (typeof this.$refs.videoPlayer === "undefined") return null;
      return this.$refs.videoPlayer.player;
    },
    playerOptions() {
      return this.getPlayerOption(this.camera.movieType) || {};
    },
    verticalNavMenuWidth() {
      return this.$store.state.verticalNavMenuWidth;
    },

    // 以下、hykeCam用 computed
    isHykeCam() {
      return this.camera.movieType === this.CAMERA_MOVIE_TYPE.HYKECAM;
    },
    hykeCamHeightPx() {
      return this.hykeCamHeight + "px";
    },
    hykeCamLoaderHeightPx() {
      return this.hykeCamLoaderHeight + "px";
    },
    hykeCamLoaderWidthPx() {
      return this.hykeCamLoaderWidth + "px";
    },
    hykeCamLoaderTopPx() {
      return this.hykeCamLoaderTop + "px";
    },
    hykeCamLoaderLeftPx() {
      return this.hykeCamLoaderLeft + "px";
    },
  },
  watch: {
    gridLayout() {
      this.repositionVjsControlBar();
    },
    gridColNum() {
      this.repositionVjsControlBar();
    },
    onChangeWindowSize() {
      this.repositionVjsControlBar();
    },
    verticalNavMenuWidth() {
      setTimeout(() => {
        this.repositionVjsControlBar();
      }, 1200);
    },
  },

  async mounted() {
    // 以下、hykeCam用 mounted
    if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.HYKECAM) {
      window.addEventListener("resize", this.hykeCamResize, false);
      this.$refs.hykeCamVideo.addEventListener(
        "loadstart",
        this.hykeCamLoadStart,
        false,
      );
      this.$refs.hykeCamVideo.addEventListener(
        "loadeddata",
        this.hykeCamLoadedData,
        false,
      );
      this.hykeCamTimeout();
      this.hykeCamTimer = setInterval(this.hykeCamTimeout, 5000);
    }

    setTimeout(() => {
      if (this.$refs.cameraInfoArea) {
        this.liveCameraAreaWidth =
          this.$refs.cameraInfoArea.getBoundingClientRect().width;
      }
    }, 2000);
  },
  async destroyed() {
    // 以下、hykeCam用 destroyed
    if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.HYKECAM) {
      clearInterval(this.hykeCamTimer);
    }
  },
  methods: {
    // camera-connectメソッド群
    async connectToCamera() {
      try {
        let res = await AiseaApiFactory.get("enterpriseCamera").connect(
          this.camera.id,
        );
        return res.data.url;
      } catch (e) {
        return null;
      }
    },
    async connectToHlsCamera() {
      try {
        let res = await AiseaApiFactory.get("enterpriseCamera").connectHLS(
          this.camera.id,
        );
        return res.data.url;
      } catch (e) {
        return null;
      }
    },

    async connect() {
      this.hasVideoError = false;
      this.cameraMsg = this.getCameraConnectingMessage(this.camera.movieType);
      let url;
      if (
        this.camera.movieType === this.CAMERA_MOVIE_TYPE.HLS_CAMERA ||
        this.camera.movieType === this.CAMERA_MOVIE_TYPE.SAFIE ||
        this.camera.movieType === this.CAMERA_MOVIE_TYPE.HLS_RADAR
      ) {
        url = await this.connectToHlsCamera();
        const resultValidMovie = await this.hasValidMovie(
          url,
          this.camera.movieType,
        );
        if (!resultValidMovie) {
          url = null;
        }
        this.hlsSrc = url;
        if (url) {
          this.playVideoHls(url);
        } else {
          this.errorHandler();
        }
      } else {
        url = await this.connectToCamera();
        this.hlsSrc = url + "/webcam/hls/stream.m3u8";
        if (url) {
          this.playVideo(url);
        } else {
          this.errorHandler();
        }
      }
    },
    playVideo(baseUrl) {
      if (this.player) {
        this.cameraMsg = "";
        const video = {
          withCredentials: false,
          type: "application/x-mpegurl",
          src: baseUrl + "/webcam/hls/stream.m3u8",
        };
        this.hlsSrc = baseUrl + "/webcam/hls/stream.m3u8";
        this.player.src(video);
        this.player.poster(baseUrl + "/webcam/thumbnail/thumbnail.png");
        this.showPlayButton();
      }
    },
    playVideoHls(baseUrl) {
      if (this.player) {
        this.cameraMsg = "";
        const video = {
          withCredentials: false,
          type: "application/x-mpegurl",
          src: baseUrl,
        };
        this.hlsSrc = baseUrl;
        this.player.src(video);
        let path = baseUrl.split("/");
        path.pop();
        this.player.poster(path.join("/") + "/thumbnail.png");
        this.showPlayButton();
      }
    },
    errorHandler() {
      if (this.player) {
        this.player.reset();
        let ErrorDisplay = videojs.getComponent("ErrorDisplay");
        this.errorDisplay = new ErrorDisplay(this.player, {
          temporary: false,
        });
        this.player.addChild(this.errorDisplay);
        this.cameraMsg = "";
        this.player.error(this.getCameraErrorMessage(this.camera.movieType));
        this.retryText = "もう一度接続";
        this.hasVideoError = true;
      }
    },

    async hasValidMovie(url, movieType) {
      const graceMinutes = 30;

      // m3u8の最後の動画ファイル名を取得する。
      const playListText = await fetch(url, { mode: "cors" }).then((res) =>
        res.text(),
      );
      const playListLines = playListText
        .split("\n")
        .filter((x) => x.match(/\.ts$/));

      if (movieType === this.CAMERA_MOVIE_TYPE.SAFIE) {
        // Safieは ファイル名から時間を割り出せないので、firestore.metadataで判定
        if (playListLines.length === 0) {
          return false;
        } else {
          const webcamChildPath = decodeURIComponent(
            playListLines[playListLines.length - 1].replace(
              new RegExp(process.env.VUE_APP_CLOUD_SOTRAGE_WEBCAM_URL),
              "",
            ),
          );
          const ref = storageWebCam.child(webcamChildPath);
          const metadata = await ref.getMetadata();
          const diffMinutes =
            (moment().valueOf() - moment(metadata["updated"]).valueOf()) /
            (60 * 1000);
          return diffMinutes < graceMinutes;
        }
      }

      const lastMovieFileName =
        playListLines.length > 0
          ? playListLines[playListLines.length - 1]
          : null;
      if (lastMovieFileName) {
        // https://storage.googleapis.com/aisea-webcam/fa2dd7ab01437c01d86726af6e7e63e2f145347fb4e4a82a6589d6260abec3f1/20220817/17/20220817-173000%2C10.ts
        // 20220817-173000%2C10.ts
        const ymdhmsZ =
          lastMovieFileName
            .replace(/^.*\//, "")
            .replace(/-/g, "_")
            .replace(/^([0-9_]+).*$/, "$1") + "Z";
        const lastMomentValue = moment(ymdhmsZ, "YYYYMMDD_HHmmssZ").valueOf();
        if (lastMomentValue + graceMinutes * 60 * 1000 > moment().valueOf()) {
          // m3u8の最後の動画ファイル名 から読み取れる時間が直近であれば、Live有効な動画と判定
          return true;
        } else {
          Vue.$log.debug(
            "movie latest timestamp",
            this.camera.id,
            this.camera.movieType,
            ymdhmsZ,
            ((moment().valueOf() - lastMomentValue) / (60 * 1000)).toFixed(1) +
              "min.",
          );
        }
      }
      return false;
    },

    // video-player用メソッド群
    async onPlayerReadied() {
      if (!this.initialized) {
        this.initialized = true;
        await this.connect();

        this.repositionVjsControlBar();

        if (![1, 2, 3].includes(this.camera.movieType) || this.forcePlayFlg) {
          setTimeout(() => {
            if (this.player) {
              this.player.play();
            }
          }, 2000);
        }
      }
    },
    onPlayerPlay() {
      this.hidePlayButton();
      this.repositionVjsControlBar();
    },
    onPlayerPause() {
      this.showPlayButton();
    },
    onPlayerError(player) {
      const errorCode = player && player.error() && player.error().code;
      if (errorCode !== 3) {
        this.cameraMsg = "";
        this.retryText = "もう一度接続";
        this.hidePlayButton();
        this.hasVideoError = true;
      } else {
        // 3: MEDIA_ERR_DECODE でエラーになった場合は 再接続をコール
        player.error(null);
        this.connect().then(() => {
          if (this.player) {
            this.player.play();
          }
        });
      }
    },

    hidePlayButton() {
      let playButton = this.player
        .contentEl()
        .getElementsByClassName("vjs-big-play-button");
      playButton[0].style.display = "none";
    },

    showPlayButton() {
      let playButton = this.player
        .contentEl()
        .getElementsByClassName("vjs-big-play-button");
      playButton[0].style.display = "block";
    },

    // 詳細画面への遷移
    toDetailClicked(cameraId, mode) {
      const nextPageName =
        mode !== "history"
          ? "enterprise-management-camera-view-realtime"
          : "enterprise-management-camera-view-history";
      const routerPush = {
        name: nextPageName,
        params: { cameraId: cameraId },
      };
      this.$router.push(routerPush);
    },

    onSelectCamera() {
      if (this.player && this.player.paused()) {
        this.player.play();
      }
      this.$emit("onSelectCamera", this.camera.id);
    },

    repositionVjsControlBar() {
      this.$nextTick(() => {
        const liveCameraArea = document
          .getElementById(
            `grid-view-live-camera-${this.camera.id}${
              this.isCloseUpCamera ? "-close-up" : ""
            }`,
          )
          .querySelector(".live-camera-area");
        if (this.$refs.cameraInfoArea) {
          this.liveCameraAreaWidth =
            this.$refs.cameraInfoArea.getBoundingClientRect().width;
        }
        if (this.$refs.videoPlayer) {
          let videoJsArea = document
            .getElementById(
              `grid-view-live-camera-${this.camera.id}${
                this.isCloseUpCamera ? "-close-up" : ""
              }`,
            )
            .querySelector(".video-js");
          const vjsControlBar = document
            .getElementById(
              `grid-view-live-camera-${this.camera.id}${
                this.isCloseUpCamera ? "-close-up" : ""
              }`,
            )
            .querySelector(".vjs-control-bar");

          if (vjsControlBar && liveCameraArea && videoJsArea) {
            let playerTop = videoJsArea.getBoundingClientRect().top;

            this.liveVideoPlayerHeight =
              Math.min(
                liveCameraArea.getBoundingClientRect().height *
                  (videoJsArea.getBoundingClientRect().width /
                    videoJsArea.getBoundingClientRect().height -
                    0.1),
                this.liveCameraAreaWidth,
              ) + "px";

            setTimeout(() => {
              // videoJsの大きさが liveVideoPlayerHeight再設定により変更されるので、再取得
              videoJsArea = document
                .getElementById(
                  `grid-view-live-camera-${this.camera.id}${
                    this.isCloseUpCamera ? "-close-up" : ""
                  }`,
                )
                .querySelector(".video-js");
              const gridHeight = liveCameraArea.getBoundingClientRect().height;
              const gridTop = liveCameraArea.getBoundingClientRect().top;
              playerTop = videoJsArea.getBoundingClientRect().top;
              vjsControlBar.style.top =
                gridHeight -
                (playerTop - gridTop) -
                this.cameraInfoBarHeight +
                8 +
                "px";

              let leftAdjust =
                Math.abs(
                  this.liveCameraAreaWidth -
                    videoJsArea.getBoundingClientRect().width,
                ) / 2;

              vjsControlBar.style.left = -1 * leftAdjust + "px";
              vjsControlBar.style.width = this.liveCameraAreaWidth + "px";
            }, 1000);
          } else if (vjsControlBar) {
            vjsControlBar.style.top = null;
          }
        }
      });
    },

    // 以下、hykeCam用 メソッド
    ...mapActions(["hykeCamGetMedia"]),

    async hykeCamTimeout() {
      let payload = {
        cameraId: this.camera.id,
      };
      let res = await this.hykeCamGetMedia(payload);
      if (this.hykeCamUrl != res.url) {
        this.hykeCamUrl = res.url;
        this.$refs.hykeCamVideo.src = this.hykeCamUrl;
      }
    },
    async hykeCamResize() {
      if (this.$refs.hykeCamVideo) {
        this.hykeCamHeight = this.$refs.hykeCamVideo.clientHeight;
      }
    },
    async hykeCamLoadStart() {
      this.hykeCamLoaderHeight = this.$refs.hykeCamCameraArea.clientHeight / 4;
      this.hykeCamLoaderWidth = this.hykeCamLoaderHeight;
      this.hykeCamLoaderTop =
        this.$refs.hykeCamCameraArea.clientHeight / 2 -
        this.hykeCamLoaderHeight / 2;
      this.hykeCamLoaderLeft =
        this.$refs.hykeCamCameraArea.clientWidth / 2 -
        this.hykeCamLoaderWidth / 2;
      this.hykeCamLoaderShow = true;
    },
    async hykeCamLoadedData() {
      this.hykeCamLoaderShow = false;
      this.hykeCamHeight = this.$refs.hykeCamVideo.clientHeight;
    },
    async hykeCamScreen() {
      const video = this.$refs.hykeCamVideo;
      if (video.requestFullScreen) {
        video.requestFullScreen();
      } else if (video.webkitRequestFullScreen) {
        video.webkitRequestFullScreen();
      } else if (video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      } else if (video.mozRequestFullScreen) {
        video.mozRequestFullScreen();
      } else if (video.msRequestFullscreen) {
        video.msRequestFullscreen();
      }
    },
  },
};
</script>

<style lang="scss">
.live-camera-area {
  .video-js .vjs-big-play-button {
    display: none;
  }
  .vjs-error .vjs-error-display:before {
    font-family: "Material Icons";
    content: "\E001";
    font-size: 480%;
  }
}
</style>
<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.camera-name-ellipsis {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.vs__dropdown-menu {
  max-height: 150px !important;
}

.live-camera-area {
  // position: relative;
  background-color: black;
}
.live-camera-area p {
  position: absolute;
  top: 50%;
  left: 50%;
  -ms-transform: translate(-50%, -50%);
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  margin: 0;
  padding: 0;
  color: white;
  z-index: 5;
}

.live-camera-area button {
  z-index: 5;
  position: absolute;
  top: 70%;
  left: 50%;
  white-space: nowrap;
  -ms-transform: translate(-50%, -50%);
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
}

.vjs-text {
  z-index: 1;
  font-size: 2em;
  line-height: 1.5em;
  height: 1.63332em;
  display: block;
  position: absolute;
  top: 10px;
  opacity: 1;
  transition: all 0.4s;
}

.camera-info-area {
  font-size: large;
  color: #7367f0;
  height: 100%;
  padding-top: 4px;
  z-index: 9;
  background-color: $aisea-black;
}

.camera-category-icon {
  width: 20px;
  height: 20px;
  margin-top: 4px;
  margin-right: 10px;
  font-size: 12px;
  border-radius: 50%;
  color: $aisea-light-white;
  white-space: nowrap;
}

.vessel-capital-letter {
  position: absolute;
  top: -16px;
  left: 4px;
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 50%;
  border: 2px solid white;
}

.detail-link-item {
  color: $aisea-black;
  &:hover {
    color: $aisea-blue;
  }
}

.hykecam-camera-area {
  background-color: black;
  position: relative;
}

.hykecam-center {
  width: 55%;
  margin: 0 auto;
}

.hykecam-video {
  width: 100%;
  z-index: 10;
}

.hykecam-loader {
  font-size: 10px;
  margin: 50px auto;
  text-indent: -9999em;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: #ffffff;
  background: -moz-linear-gradient(
    left,
    #ffffff 10%,
    rgba(255, 255, 255, 0) 42%
  );
  background: -webkit-linear-gradient(
    left,
    #ffffff 10%,
    rgba(255, 255, 255, 0) 42%
  );
  background: -o-linear-gradient(left, #ffffff 10%, rgba(255, 255, 255, 0) 42%);
  background: -ms-linear-gradient(
    left,
    #ffffff 10%,
    rgba(255, 255, 255, 0) 42%
  );
  background: linear-gradient(
    to right,
    #ffffff 10%,
    rgba(255, 255, 255, 0) 42%
  );
  position: absolute;
  top: 20%;
  left: 40%;
  -webkit-animation: load3 1.4s infinite linear;
  animation: load3 1.4s infinite linear;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
  z-index: 20;
}
.hykecam-loader:before {
  width: 50%;
  height: 50%;
  background: #ffffff;
  border-radius: 100% 0 0 0;
  position: absolute;
  top: 0;
  left: 0;
  content: "";
}
.hykecam-loader:after {
  background: #000000;
  width: 75%;
  height: 75%;
  border-radius: 50%;
  content: "";
  margin: auto;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}
@-webkit-keyframes load3 {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@keyframes load3 {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
</style>
